import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Briefcase, Loader2, RefreshCw, AlertTriangle, Check, Plus, Copy, Trash2, Save,
  ChevronUp, ChevronDown, Lock, Search,
} from 'lucide-react';
import { apiService, ConfigInspection, ConfigSectionsQuery, ContainerApplyResult } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { loadPackPalette, PalettePickEntry } from '@/components/config/packPalette';
import { SearchPicker, PickerOption } from '@/components/config/SearchPicker';
import { ApplyReport, ContainerScope, NoReference } from '@/components/config/AllContainers';
import styles from './LootboxPage.module.scss';

// ============================================================
// RecubeJobs : jobs.config.json de RecubeJobsMod, commun à tous les containers du projet
// ============================================================
// RecubeJobsMod n'est PAS JobsMod : autre mod, autre format (ServerSettings, NgUi), même nom de
// fichier au même endroit (<ModDirectory>/jobs.config.json). La page Jobs édite celui de JobsMod ;
// celle-ci ne voit que les containers dont le release-version.json porte RecubeJobsMod (requête
// `mod`), et le serveur ne lit ni n'écrit d'autres sections que les trois éditées ici :
//
//   JobRewards  : JobRewards[<métier>][<niveau>] = [ { Type, ItemId, NameItem, Amount, RecipeId, UsageId } ]
//                 (UnlockCraft lit RecipeId, UnlockUsage lit UsageId)
//   DailyQuests : { QuestsPerDay, Quests: [ { Type, IconId, TargetId, Name, Description, JobId,
//                 TargetValue, XpReward } ] }
//   NgUi        : écran compétences NationsGlory : NavCommands, ClassementCommand, ItemTextures et,
//                 par métier, Description et Tiers [ { Level, Name, Advantages[5 max], Texture } ]
//
// ServerSettings.Server (paladium, nationsglory) identifie le serveur : affiché, jamais écrit.
// DatabaseSettings (chaîne de connexion Mongo) et ProfileApiSettings (URL interne) sont propres à
// chaque serveur : ni renvoyés par le serveur, ni écrits, quel que soit ce que la page demande.
// ============================================================

const RECUBE_FILE = 'jobs.config.json';
const RECUBE_KEYS = ['JobRewards', 'DailyQuests', 'NgUi'] as const;
type RecubeKey = (typeof RECUBE_KEYS)[number];

const RECUBE_QUERY: ConfigSectionsQuery = {
  fileNames: [RECUBE_FILE],
  keys: [...RECUBE_KEYS],
  mod: 'RecubeJobsMod',
};

const SECTION_LABELS: Record<string, string> = {
  JobRewards: 'récompenses',
  DailyQuests: 'quêtes du jour',
  NgUi: 'écran NationsGlory',
};

/** Métiers du mod (JobType) : une clé inconnue est ignorée par le mod au chargement. */
const KNOWN_JOBS = ['miner', 'farmer', 'builder', 'lumberjack', 'hunter', 'engineer'];
const JOB_LABEL: Record<string, string> = {
  miner: 'Mineur',
  farmer: 'Agriculteur',
  builder: 'Constructeur',
  lumberjack: 'Bûcheron',
  hunter: 'Chasseur',
  engineer: 'Ingénieur',
};
const jobLabel = (j: string) => (JOB_LABEL[j] ? `${JOB_LABEL[j]} (${j})` : j);

const REWARD_TYPES = ['Money', 'Item', 'UnlockCraft', 'UnlockUsage'] as const;
type RewardType = (typeof REWARD_TYPES)[number];
const REWARD_LABEL: Record<RewardType, string> = {
  Money: 'Argent',
  Item: 'Item',
  UnlockCraft: 'Débloque un craft',
  UnlockUsage: 'Débloque une utilisation',
};

const QUEST_TYPES = ['break', 'kill', 'smelt', 'craft'] as const;
const QUEST_LABEL: Record<string, string> = { break: 'Casser', kill: 'Tuer', smelt: 'Cuire', craft: 'Crafter' };

/** Onglets de la nav bar de l'écran compétences (UI_LAYOUT.md du mod). */
const NAV_TABS = ['inventaire', 'carte', 'profil', 'cosmetique'];
const NAV_LABEL: Record<string, string> = {
  inventaire: 'Inventaire', carte: 'Carte', profil: 'Profil', cosmetique: 'Cosmétique',
};

/** Le client affiche 5 paliers par métier, 5 avantages par palier, la texture des paliers 3 et 5. */
const MAX_TIERS = 5;
const MAX_ADVANTAGES = 5;
const TEXTURE_TIERS = [2, 4];

interface Reward {
  Type?: string;
  ItemId?: string;
  NameItem?: string;
  Amount?: number;
  RecipeId?: string;
  UsageId?: string;
  [k: string]: unknown;
}

interface Quest {
  Type?: string;
  IconId?: string;
  TargetId?: string;
  Name?: string;
  Description?: string;
  JobId?: string;
  TargetValue?: number;
  XpReward?: number;
  [k: string]: unknown;
}

interface DailyQuests {
  QuestsPerDay?: number;
  Quests?: Quest[];
  [k: string]: unknown;
}

interface Tier {
  Level?: number;
  Name?: string;
  Advantages?: string[];
  Texture?: string | null;
  [k: string]: unknown;
}

interface NgJob {
  Description?: string;
  Tiers?: Tier[];
  [k: string]: unknown;
}

interface NgUi {
  NavCommands?: Record<string, string>;
  ClassementCommand?: string | null;
  ItemTextures?: Record<string, string>;
  Jobs?: Record<string, NgJob>;
  [k: string]: unknown;
}

/** Ce que la page édite : les trois sections, chacune seulement si la référence la porte. */
interface RecubeSections {
  JobRewards?: Record<string, Record<string, Reward[]>>;
  DailyQuests?: DailyQuests;
  NgUi?: NgUi;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/** Clé d'identifiant débloqué : RecipeId pour un craft, UsageId pour une utilisation (RewardService du mod). */
function unlockKey(t: string | undefined): 'RecipeId' | 'UsageId' | null {
  if (t === 'UnlockCraft') return 'RecipeId';
  if (t === 'UnlockUsage') return 'UsageId';
  return null;
}

/** Entier positif saisi : le mod lie ces champs à des int, un décimal ou un texte ferait échouer son chargement. */
function toInt(raw: string, fallback = 0): number {
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : fallback;
}

function sortedLevels(levels: Record<string, unknown> | undefined): string[] {
  return Object.keys(levels ?? {}).sort((a, b) => Number(a) - Number(b));
}

/** Copie d'un objet avec la clé posée, ou retirée quand la valeur est vide. */
function withOptional<T extends Record<string, unknown>>(obj: T, key: string, value: string): T {
  const out = { ...obj } as Record<string, unknown>;
  if (value.trim() === '') delete out[key];
  else out[key] = value;
  return out as T;
}

/** Renomme une clé d'un objet sans changer l'ordre des autres (identifiant d'item d'ItemTextures). */
function renameKey(obj: Record<string, string>, from: string, to: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) out[k === from ? to : k] = v;
  return out;
}

export function RecubeJobsPage() {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const [inspection, setInspection] = useState<ConfigInspection | null>(null);
  const [doc, setDoc] = useState<RecubeSections | null>(null);
  const [orig, setOrig] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [report, setReport] = useState<ContainerApplyResult | null>(null);
  const [palette, setPalette] = useState<PalettePickEntry[]>([]);
  const [tab, setTab] = useState<'rewards' | 'quests' | 'ui'>('rewards');
  const [job, setJob] = useState('');
  const [level, setLevel] = useState('');
  const [questJob, setQuestJob] = useState('');
  const [uiJob, setUiJob] = useState('');
  const [textureFilter, setTextureFilter] = useState('');
  /** Dernière lecture lancée : une réponse plus ancienne (projet précédent) est ignorée. */
  const loadSeq = useRef(0);

  // Même liste que les catalogues Blocks / Items du projet, avec les vignettes.
  useEffect(() => {
    loadPackPalette(activeGameId).then(setPalette).catch(() => setPalette([]));
  }, [activeGameId]);

  const itemOptions = useMemo<PickerOption[]>(
    () => palette.map((p) => ({
      value: p.id,
      label: p.id,
      hint: p.kind === 'block' ? 'block' : 'item',
      thumbnailPath: p.path,
      groupId: p.groupId,
    })),
    [palette],
  );

  /**
   * Lit les trois sections dans le container de référence (le premier, par id, dont l'image porte
   * RecubeJobsMod et son fichier) et l'alignement des autres. `keepEdit` ne rafraîchit que le
   * bandeau, après un enregistrement, sans toucher au document en cours d'édition.
   */
  const load = useCallback(async (keepEdit = false) => {
    const seq = ++loadSeq.current;
    setLoading(true);
    if (!keepEdit) {
      setStatus(null);
      setReport(null);
      setDoc(null);
    }
    const r = await apiService.inspectContainerConfig(RECUBE_QUERY);
    if (seq !== loadSeq.current) return;
    setLoading(false);
    setInspection(r.data ?? null);
    if (keepEdit) return;
    if (!r.data) { setStatus({ ok: false, msg: r.error ?? 'Lecture impossible.' }); return; }
    if (!r.data.referenceContainerId) return; // aucun container ne porte le mod et son fichier : dit plus bas
    const raw = (r.data.sections ?? {}) as Record<string, unknown>;
    const next: RecubeSections = {};
    const malformed: string[] = [];
    for (const k of RECUBE_KEYS) {
      if (raw[k] === undefined) continue;
      if (isObject(raw[k])) (next as Record<string, unknown>)[k] = raw[k];
      else malformed.push(k);
    }
    if (malformed.length > 0) {
      setStatus({ ok: false, msg: `${r.data.referencePath ?? RECUBE_FILE} : ${malformed.join(', ')} n'a pas la forme attendue (objet). Corrige le fichier dans Paramètres généraux → Containers.` });
      return;
    }
    if (Object.keys(next).length === 0) {
      setStatus({ ok: false, msg: `${r.data.referencePath ?? RECUBE_FILE} ne contient ni JobRewards, ni DailyQuests, ni NgUi.` });
      return;
    }
    setDoc(next);
    setOrig(JSON.stringify(next));
    const firstJob = Object.keys(next.JobRewards ?? {})[0] ?? '';
    setJob(firstJob);
    setLevel(sortedLevels(next.JobRewards?.[firstJob])[0] ?? '');
    setQuestJob('');
    setUiJob(Object.keys(next.NgUi?.Jobs ?? {})[0] ?? '');
    setTextureFilter('');
  }, []);

  useEffect(() => { void load(); }, [activeGameId, load]);

  const serialized = doc ? JSON.stringify(doc) : '';
  const dirty = !!doc && serialized !== orig;

  /** Champs en lecture seule déclarés par le mod (ServerSettings.Server). */
  const server = inspection?.readOnly?.ServerSettings?.Server;
  /** Autres sections du fichier : propres à chaque serveur, jamais envoyées ici. */
  const hiddenKeys = (inspection?.otherKeys ?? []).filter((k) => k !== 'ServerSettings');
  /** Sections absentes de la référence : la page ne les crée ni ne les retire. */
  const untouched = RECUBE_KEYS.filter((k) => !doc || doc[k] === undefined);

  // ==================== Récompenses (JobRewards) ====================

  const rewardsMap = doc?.JobRewards;
  const jobs = Object.keys(rewardsMap ?? {});
  const levels = useMemo(() => sortedLevels(rewardsMap?.[job]), [rewardsMap, job]);
  const rewards: Reward[] = Array.isArray(rewardsMap?.[job]?.[level]) ? rewardsMap![job][level] : [];
  /** Niveaux des paliers de l'écran NationsGlory pour ce métier, pour aligner récompenses et cartes. */
  const jobTiers = doc?.NgUi?.Jobs?.[job]?.Tiers;
  const tierLevels = (Array.isArray(jobTiers) ? jobTiers : [])
    .map((t) => t?.Level)
    .filter((l): l is number => typeof l === 'number');

  const setRewardsMap = (fn: (m: Record<string, Record<string, Reward[]>>) => Record<string, Record<string, Reward[]>>) => {
    setDoc((prev) => (prev?.JobRewards ? { ...prev, JobRewards: fn(prev.JobRewards) } : prev));
  };

  const setRewards = (next: Reward[]) => {
    setRewardsMap((m) => ({ ...m, [job]: { ...m[job], [level]: next } }));
  };

  const mutateReward = (i: number, patch: (r: Reward) => Reward) => {
    const next = [...rewards];
    next[i] = patch({ ...next[i] });
    setRewards(next);
  };

  const setRewardType = (i: number, t: RewardType) => {
    mutateReward(i, (r) => {
      const out: Reward = { ...r, Type: t };
      const carried = r.RecipeId ?? r.UsageId ?? r.ItemId ?? '';
      delete out.RecipeId;
      delete out.UsageId;
      const key = unlockKey(t);
      if (key) out[key] = carried;
      if (t === 'Item' && out.ItemId === undefined) out.ItemId = '';
      return out;
    });
  };

  const moveIn = <T,>(list: T[], i: number, d: number): T[] | null => {
    const j = i + d;
    if (j < 0 || j >= list.length) return null;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  };

  const addRewardLevel = () => {
    const next = String((levels.length ? Math.max(...levels.map(Number)) : 0) + 1);
    setRewardsMap((m) => ({ ...m, [job]: { ...m[job], [next]: [] } }));
    setLevel(next);
  };

  const removeRewardLevel = () => {
    if (!level || !window.confirm(`Supprimer le palier niveau ${level} de ${jobLabel(job)} et ses récompenses ?`)) return;
    setRewardsMap((m) => {
      const lv = { ...m[job] };
      delete lv[level];
      return { ...m, [job]: lv };
    });
    setLevel(levels.filter((l) => l !== level)[0] ?? '');
  };

  const addRewardJob = (j: string) => {
    if (!j || jobs.includes(j)) return;
    setRewardsMap((m) => ({ ...m, [j]: {} }));
    setJob(j);
    setLevel('');
  };

  // ==================== Quêtes du jour (DailyQuests) ====================

  const daily = doc?.DailyQuests;
  const quests: Quest[] = Array.isArray(daily?.Quests) ? daily!.Quests! : [];
  const questJobs = useMemo(
    () => [...new Set([...KNOWN_JOBS, ...quests.map((q) => q.JobId ?? '').filter(Boolean)])],
    [quests],
  );

  const setDaily = (fn: (d: DailyQuests) => DailyQuests) => {
    setDoc((prev) => (prev?.DailyQuests ? { ...prev, DailyQuests: fn(prev.DailyQuests) } : prev));
  };

  const setQuests = (next: Quest[]) => setDaily((d) => ({ ...d, Quests: next }));

  const mutateQuest = (i: number, patch: (q: Quest) => Quest) => {
    const next = [...quests];
    next[i] = patch({ ...next[i] });
    setQuests(next);
  };

  const addQuest = () => setQuests([...quests, {
    Type: 'break', IconId: '', TargetId: '', Name: '', Description: '',
    JobId: questJob || 'miner', TargetValue: 1, XpReward: 100,
  }]);

  const shownQuests = quests
    .map((q, i) => ({ q, i }))
    .filter(({ q }) => !questJob || q.JobId === questJob);

  /** Monte ou descend une quête parmi celles affichées (le filtre par métier masque les autres). */
  const moveQuest = (pos: number, d: number) => {
    const a = shownQuests[pos];
    const b = shownQuests[pos + d];
    if (!a || !b) return;
    const next = [...quests];
    [next[a.i], next[b.i]] = [next[b.i], next[a.i]];
    setQuests(next);
  };
  const questsPerDay = typeof daily?.QuestsPerDay === 'number' ? daily.QuestsPerDay : undefined;

  // ==================== Écran NationsGlory (NgUi) ====================

  const ui = doc?.NgUi;
  const navKeys = [...new Set([...NAV_TABS, ...Object.keys(ui?.NavCommands ?? {})])];
  const uiJobs = Object.keys(ui?.Jobs ?? {});
  const ngJob: NgJob | undefined = ui?.Jobs?.[uiJob];
  const tiers: Tier[] = Array.isArray(ngJob?.Tiers) ? ngJob!.Tiers! : [];
  const textures = Object.entries(ui?.ItemTextures ?? {});
  const shownTextures = textures
    .map(([k, v], i) => ({ k, v, i }))
    .filter(({ k, v }) => {
      const f = textureFilter.trim().toLowerCase();
      return !f || k.toLowerCase().includes(f) || v.toLowerCase().includes(f);
    });

  const setUi = (fn: (u: NgUi) => NgUi) => {
    setDoc((prev) => (prev?.NgUi ? { ...prev, NgUi: fn(prev.NgUi) } : prev));
  };

  const setNgJob = (fn: (j: NgJob) => NgJob) => {
    setUi((u) => ({ ...u, Jobs: { ...(u.Jobs ?? {}), [uiJob]: fn({ ...(u.Jobs?.[uiJob] ?? {}) }) } }));
  };

  const setTiers = (next: Tier[]) => setNgJob((j) => ({ ...j, Tiers: next }));

  const mutateTier = (i: number, patch: (t: Tier) => Tier) => {
    const next = [...tiers];
    next[i] = patch({ ...next[i] });
    setTiers(next);
  };

  const addTier = () => {
    const last = tiers.reduce((m, t) => Math.max(m, typeof t.Level === 'number' ? t.Level : 0), 0);
    setTiers([...tiers, { Level: last + 1, Name: '', Advantages: [] }]);
  };

  const addUiJob = (j: string) => {
    if (!j || uiJobs.includes(j)) return;
    setUi((u) => ({ ...u, Jobs: { ...(u.Jobs ?? {}), [j]: { Description: '', Tiers: [] } } }));
    setUiJob(j);
  };

  const setTextures = (next: Record<string, string>) => setUi((u) => ({ ...u, ItemTextures: next }));

  const textureKeysBad = textures.some(([k]) => k.trim() === '');
  const navBad = Object.keys(ui?.NavCommands ?? {}).some((k) => k.trim() === '');

  // ==================== Sauvegarde ====================

  /**
   * Écrit les sections présentes dans chaque container du projet dont l'image porte RecubeJobsMod et
   * son fichier. Une section absente de la référence n'est jamais créée ni retirée ailleurs.
   */
  const save = async () => {
    if (!doc) return;
    if (textureKeysBad || navBad) {
      setStatus({ ok: false, msg: "Une icône d'item ou un onglet de l'écran NationsGlory n'a pas d'identifiant : complète-le ou supprime la ligne." });
      return;
    }
    setSaving(true);
    setStatus(null);
    setReport(null);
    const sections: Record<string, unknown> = {};
    for (const k of RECUBE_KEYS) {
      if (doc[k] !== undefined) sections[k] = doc[k];
    }
    const res = await apiService.applyContainerConfig({ ...RECUBE_QUERY, sections });
    setSaving(false);
    if (!res.data) {
      setStatus({ ok: false, msg: res.error ?? 'Enregistrement en échec' });
      return;
    }
    setReport(res.data);
    // Un container en échec garde la page « non enregistrée » : un nouvel essai réécrit partout.
    if (res.data.success) setOrig(serialized);
    void load(true);
  };

  /** Relit jobs.config.json ; ne jette pas des modifications sans le demander. */
  const reload = () => {
    if (dirty && !window.confirm('Recharger jobs.config.json abandonnera les modifications non enregistrées. Continuer ?')) return;
    void load();
  };

  /** Lecture faite, mais aucun container du projet ne porte le mod et son fichier : le pourquoi, container par container. */
  const missing = inspection && !inspection.referenceContainerId ? inspection : null;

  const sectionMissing = (k: RecubeKey) => (
    <div className={styles.detailEmpty}>
      <AlertTriangle size={22} />
      <p>Section <code>{k}</code> absente de {inspection?.referencePath ?? RECUBE_FILE} : la page ne la crée pas.</p>
    </div>
  );

  const rowActions = (onUp: () => void, onDown: () => void, onCopy: () => void, onDelete: () => void) => (
    <span className={styles.rowActions}>
      <button className={styles.iconBtn} title="Monter" onClick={onUp}><ChevronUp size={13} /></button>
      <button className={styles.iconBtn} title="Descendre" onClick={onDown}><ChevronDown size={13} /></button>
      <button className={styles.iconBtn} title="Dupliquer" onClick={onCopy}><Copy size={13} /></button>
      <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} title="Supprimer" onClick={onDelete}><Trash2 size={13} /></button>
    </span>
  );

  const footer = (extra?: ReactNode) => (
    <div className={styles.footerActions}>
      {extra ?? <span />}
      <span className={styles.muted}>{dirty ? 'Modifications non enregistrées.' : 'Enregistré.'}</span>
    </div>
  );

  const subtitle = tab === 'rewards'
    ? "Récompenses de palier par métier de RecubeJobsMod (argent, items, déblocages de craft et d'utilisation)."
    : tab === 'quests'
    ? 'Quêtes du jour de RecubeJobsMod : le mod en tire chaque jour un nombre fixe par joueur.'
    : "Écran compétences de NationsGlory : commandes de la barre de navigation, description et paliers de chaque métier, icônes des items.";

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>RecubeJobs</h1>
          <p className={styles.subtitle}>
            {subtitle} Commun à tous les containers du projet dont l'image porte RecubeJobsMod. La base de données,
            l'API des profils et le serveur ne sont ni affichés ni modifiés ici.
          </p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.secondaryBtn} onClick={reload} disabled={loading}>
            {loading ? <Loader2 size={14} className={styles.spin} /> : <RefreshCw size={14} />} Rafraîchir
          </button>
          <button className={styles.primaryBtn} onClick={save} disabled={!dirty || saving}
            title="Écrit les récompenses, les quêtes et l'écran NationsGlory dans tous les containers du projet qui portent RecubeJobsMod">
            {saving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />} Enregistrer
          </button>
        </div>
      </header>

      <nav className={styles.tabBar}>
        <button type="button" className={`${styles.tabBtn} ${tab === 'rewards' ? styles.tabBtnOn : ''}`}
          onClick={() => setTab('rewards')}>Récompenses</button>
        <button type="button" className={`${styles.tabBtn} ${tab === 'quests' ? styles.tabBtnOn : ''}`}
          onClick={() => setTab('quests')}>Quêtes du jour</button>
        <button type="button" className={`${styles.tabBtn} ${tab === 'ui' ? styles.tabBtnOn : ''}`}
          onClick={() => setTab('ui')}>Écran NationsGlory</button>
      </nav>

      {inspection && (
        <ContainerScope entries={inspection.entries} referencePath={inspection.referencePath}
          labelOf={(k) => SECTION_LABELS[k] ?? k} untouched={untouched} />
      )}

      {doc && (server !== undefined || hiddenKeys.length > 0) && (
        <p className={styles.muted}>
          {server !== undefined && (
            <>
              <Lock size={12} aria-hidden /> Serveur (<code>ServerSettings.Server</code>) : <strong>{String(server)}</strong>,
              en lecture seule : propre à chaque serveur, il choisit l'écran que /job ouvre.{' '}
            </>
          )}
          {hiddenKeys.length > 0 && <>Sections propres à chaque serveur, ni affichées ni modifiées : {hiddenKeys.join(', ')}.</>}
        </p>
      )}

      {status && (
        <div className={status.ok ? styles.ok : styles.error}>
          {status.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {status.msg}
        </div>
      )}

      {report && <ApplyReport result={report} onClose={() => setReport(null)} />}

      <section className={styles.detail}>
        {!doc ? (
          <div className={styles.detailEmpty}>
            {loading ? (
              <><Loader2 size={22} className={styles.spin} /><p>Ouverture de {RECUBE_FILE}…</p></>
            ) : missing && missing.entries.length === 0 ? (
              <>
                <AlertTriangle size={22} />
                <p>{missing.message}</p>
                <p className={styles.muted}>Déclare les containers du projet dans Paramètres généraux → Containers.</p>
              </>
            ) : missing ? (
              <>
                <NoReference file={RECUBE_FILE} entries={missing.entries} mod="RecubeJobsMod" />
                <p className={styles.muted}>
                  JobsMod lit un fichier du même nom : il s'édite dans <Link to="/config/jobs">Configuration → Jobs</Link>.
                </p>
              </>
            ) : (
              <>
                <Briefcase size={26} />
                {/* Une ouverture en échec a déjà posé son message au-dessus : ne pas le contredire. */}
                <p>{RECUBE_FILE} n'a pas pu être ouvert.</p>
              </>
            )}
          </div>
        ) : tab === 'rewards' ? (
          !rewardsMap ? sectionMissing('JobRewards') : (
            <>
              <div className={styles.boxHead}>
                <label className={styles.field}>
                  <span className={styles.label}>Métier</span>
                  <select className={styles.input} value={job}
                    onChange={(e) => {
                      const j = e.target.value;
                      setJob(j);
                      setLevel(sortedLevels(rewardsMap[j])[0] ?? '');
                    }}>
                    {jobs.map((j) => <option key={j} value={j}>{jobLabel(j)}</option>)}
                  </select>
                </label>
                <label className={styles.field}>
                  <span className={styles.label}>Palier</span>
                  <select className={styles.input} value={level} onChange={(e) => setLevel(e.target.value)}>
                    {levels.length === 0 && <option value="">Aucun palier</option>}
                    {levels.map((l) => <option key={l} value={l}>Niveau {l}</option>)}
                  </select>
                </label>
                <div className={styles.totals}>
                  <span><strong>{levels.length}</strong> palier(s)</span>
                  <span><strong>{rewards.length}</strong> récompense(s) sur ce palier</span>
                  <button className={styles.secondaryBtn} onClick={addRewardLevel} disabled={!job}><Plus size={13} /> Palier</button>
                  <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} title="Supprimer ce palier"
                    onClick={removeRewardLevel} disabled={!level}>
                    <Trash2 size={13} />
                  </button>
                  {KNOWN_JOBS.some((j) => !jobs.includes(j)) && (
                    <select className={styles.input} value="" style={{ width: 'auto' }}
                      onChange={(e) => addRewardJob(e.target.value)} aria-label="Ajouter un métier">
                      <option value="">+ Métier</option>
                      {KNOWN_JOBS.filter((j) => !jobs.includes(j)).map((j) => <option key={j} value={j}>{jobLabel(j)}</option>)}
                    </select>
                  )}
                </div>
              </div>

              {tierLevels.length > 0 && (
                <p className={styles.muted}>
                  Paliers de l'écran NationsGlory pour ce métier : niveaux {tierLevels.join(', ')}.
                  {level && !tierLevels.includes(Number(level)) && ' Ce palier de récompenses n\'a pas de carte à son niveau.'}
                </p>
              )}

              <div className={styles.tableHeadJobs}>
                <span>Type</span>
                <span>Item</span>
                <span>Quantité / montant</span>
                <span>Libellé affiché</span>
                <span>Identifiant débloqué</span>
                <span />
              </div>

              {rewards.length === 0 && <p className={styles.muted}>Aucune récompense sur ce palier.</p>}

              {rewards.map((r, i) => {
                const type = (REWARD_TYPES.includes(r.Type as RewardType) ? r.Type : 'Item') as RewardType;
                const key = unlockKey(type);
                return (
                  <div key={i} className={styles.rowEdit}>
                    <div className={styles.cellsJobs}>
                      <span>
                        <select className={styles.input} value={type} onChange={(e) => setRewardType(i, e.target.value as RewardType)}>
                          {!REWARD_TYPES.includes(r.Type as RewardType) && r.Type && <option value={type}>{r.Type} (inconnu, lu comme Item)</option>}
                          {REWARD_TYPES.map((t) => <option key={t} value={t}>{REWARD_LABEL[t]}</option>)}
                        </select>
                      </span>
                      <span className={styles.picker}>
                        <SearchPicker
                          groupId={palette[0]?.groupId ?? ''}
                          value={r.ItemId ?? ''}
                          options={itemOptions}
                          showThumbnails
                          placeholder={type === 'Money' ? 'icône (facultatif)' : type === 'Item' ? 'minecraft:diamond' : 'item affiché'}
                          onChange={(v) => mutateReward(i, (x) => {
                            // Argent : l'item n'est qu'une icône, un champ vidé n'est pas écrit.
                            const out = type === 'Money' ? withOptional(x, 'ItemId', v) : { ...x, ItemId: v };
                            // Sur un déblocage, l'identifiant suit l'item tant qu'il n'a pas été personnalisé.
                            if (key && (!x[key] || x[key] === x.ItemId)) out[key] = v;
                            return out;
                          })}
                        />
                      </span>
                      <span>
                        <input className={styles.input} type="number" min={0} step={1} value={r.Amount ?? 1}
                          onChange={(e) => mutateReward(i, (x) => ({ ...x, Amount: toInt(e.target.value, 1) }))} />
                      </span>
                      <span>
                        <input className={styles.input} value={r.NameItem ?? ''}
                          placeholder={type === 'Money' ? 'facultatif' : 'Pioche en fer'}
                          onChange={(e) => mutateReward(i, (x) => withOptional(x, 'NameItem', e.target.value))} />
                      </span>
                      <span>
                        <input className={styles.input} value={key ? String(r[key] ?? '') : ''} disabled={!key}
                          title={key ? `${key} : ${type === 'UnlockCraft' ? 'recette débloquée' : 'item dont l\'utilisation est débloquée'}` : undefined}
                          placeholder={key === 'RecipeId' ? 'recette (RecipeId)' : key === 'UsageId' ? 'item (UsageId)' : 'sans objet'}
                          onChange={(e) => key && mutateReward(i, (x) => ({ ...x, [key]: e.target.value }))} />
                      </span>
                      {rowActions(
                        () => { const n = moveIn(rewards, i, -1); if (n) setRewards(n); },
                        () => { const n = moveIn(rewards, i, 1); if (n) setRewards(n); },
                        () => { const n = [...rewards]; n.splice(i + 1, 0, JSON.parse(JSON.stringify(rewards[i])) as Reward); setRewards(n); },
                        () => setRewards(rewards.filter((_, k) => k !== i)),
                      )}
                    </div>
                  </div>
                );
              })}

              {footer(
                <button className={styles.secondaryBtn} onClick={() => setRewards([...rewards, { Type: 'Item', ItemId: '', Amount: 1 }])}
                  disabled={!level}>
                  <Plus size={14} /> Ajouter une récompense
                </button>,
              )}
            </>
          )
        ) : tab === 'quests' ? (
          !daily ? sectionMissing('DailyQuests') : (
            <>
              <div className={styles.boxHead}>
                <label className={`${styles.field} ${styles.fieldNarrow}`}>
                  <span className={styles.label}>Quêtes par jour</span>
                  <input className={styles.input} type="number" min={0} step={1} value={questsPerDay ?? ''}
                    placeholder="3"
                    onChange={(e) => {
                      const raw = e.target.value.trim();
                      setDaily((d) => {
                        const out = { ...d };
                        if (raw === '') delete out.QuestsPerDay;
                        else out.QuestsPerDay = toInt(raw, 3);
                        return out;
                      });
                    }} />
                </label>
                <label className={styles.field}>
                  <span className={styles.label}>Métier affiché</span>
                  <select className={styles.input} value={questJob} onChange={(e) => setQuestJob(e.target.value)}>
                    <option value="">Tous les métiers</option>
                    {questJobs.map((j) => <option key={j} value={j}>{jobLabel(j)}</option>)}
                  </select>
                </label>
                <div className={styles.totals}>
                  <span><strong>{quests.length}</strong> quête(s) au total</span>
                  {questJob && <span><strong>{shownQuests.length}</strong> pour ce métier</span>}
                </div>
              </div>

              {questsPerDay !== undefined && questsPerDay > quests.length && (
                <div className={styles.warnBox}>
                  <AlertTriangle size={14} /> {questsPerDay} quêtes par jour pour {quests.length} quête(s) possibles : le mod
                  n'en attribuera pas plus qu'il n'en existe.
                </div>
              )}

              <div className={styles.tableHeadQuest}>
                <span>Type</span>
                <span>Métier</span>
                <span>Cible</span>
                <span>Icône</span>
                <span>Objectif</span>
                <span>XP gagnée</span>
                <span />
              </div>

              {shownQuests.length === 0 && <p className={styles.muted}>Aucune quête{questJob ? ' pour ce métier' : ''}.</p>}

              {shownQuests.map(({ q, i }, pos) => {
                const warn = !q.TargetId || !q.Name;
                return (
                  <div key={i} className={`${styles.rowEdit} ${warn ? styles.rowWarn : ''}`}>
                    <div className={styles.cellsQuest}>
                      <span>
                        <select className={styles.input} value={q.Type ?? ''}
                          onChange={(e) => mutateQuest(i, (x) => ({ ...x, Type: e.target.value }))}>
                          {!QUEST_TYPES.includes(q.Type as (typeof QUEST_TYPES)[number]) && (
                            <option value={q.Type ?? ''}>{q.Type ? `${q.Type} (inconnu)` : 'choisir'}</option>
                          )}
                          {QUEST_TYPES.map((t) => <option key={t} value={t}>{QUEST_LABEL[t]}</option>)}
                        </select>
                      </span>
                      <span>
                        <select className={styles.input} value={q.JobId ?? ''}
                          onChange={(e) => mutateQuest(i, (x) => ({ ...x, JobId: e.target.value }))}>
                          {!questJobs.includes(q.JobId ?? '') && <option value={q.JobId ?? ''}>{q.JobId || 'choisir'}</option>}
                          {questJobs.map((j) => <option key={j} value={j}>{JOB_LABEL[j] ?? j}</option>)}
                        </select>
                      </span>
                      <span className={styles.picker}>
                        <SearchPicker groupId={palette[0]?.groupId ?? ''} value={q.TargetId ?? ''} options={itemOptions}
                          placeholder={q.Type === 'kill' ? 'minecraft:zombie' : 'minecraft:iron_ore'}
                          onChange={(v) => mutateQuest(i, (x) => ({ ...x, TargetId: v }))} />
                      </span>
                      <span className={styles.picker}>
                        <SearchPicker groupId={palette[0]?.groupId ?? ''} value={q.IconId ?? ''} options={itemOptions}
                          showThumbnails placeholder="minecraft:iron_ore"
                          onChange={(v) => mutateQuest(i, (x) => ({ ...x, IconId: v }))} />
                      </span>
                      <span>
                        <input className={styles.input} type="number" min={0} step={1} value={q.TargetValue ?? 0}
                          onChange={(e) => mutateQuest(i, (x) => ({ ...x, TargetValue: toInt(e.target.value) }))} />
                      </span>
                      <span>
                        <input className={styles.input} type="number" min={0} step={1} value={q.XpReward ?? 0}
                          onChange={(e) => mutateQuest(i, (x) => ({ ...x, XpReward: toInt(e.target.value) }))} />
                      </span>
                      {rowActions(
                        () => moveQuest(pos, -1),
                        () => moveQuest(pos, 1),
                        () => { const n = [...quests]; n.splice(i + 1, 0, JSON.parse(JSON.stringify(q)) as Quest); setQuests(n); },
                        () => setQuests(quests.filter((_, k) => k !== i)),
                      )}
                    </div>
                    <div className={styles.cellsQuestText}>
                      <input className={styles.input} value={q.Name ?? ''} placeholder="Nom de la quête (Mineur de fer)"
                        onChange={(e) => mutateQuest(i, (x) => ({ ...x, Name: e.target.value }))} />
                      <input className={styles.input} value={q.Description ?? ''} placeholder="Description (Casser 16 minerais de fer)"
                        onChange={(e) => mutateQuest(i, (x) => ({ ...x, Description: e.target.value }))} />
                    </div>
                  </div>
                );
              })}

              {footer(
                <button className={styles.secondaryBtn} onClick={addQuest}>
                  <Plus size={14} /> Ajouter une quête
                </button>,
              )}
            </>
          )
        ) : !ui ? sectionMissing('NgUi') : (
          <>
            <div className={styles.tableHeadJobs} style={{ gridTemplateColumns: '1fr' }}>
              <span>Barre de navigation</span>
            </div>
            <div className={styles.fieldsGrid} style={{ marginTop: 8, marginBottom: 16 }}>
              {navKeys.map((k) => (
                <label key={k} className={styles.field} title={`NavCommands.${k}`}>
                  <span className={styles.label}>Onglet {NAV_LABEL[k] ?? k}</span>
                  <input className={styles.input} value={ui.NavCommands?.[k] ?? ''} placeholder={`/${k}`}
                    onChange={(e) => setUi((u) => ({ ...u, NavCommands: withOptional({ ...(u.NavCommands ?? {}) }, k, e.target.value) }))} />
                </label>
              ))}
              <label className={styles.field} title="ClassementCommand">
                <span className={styles.label}>Bouton classement</span>
                <input className={styles.input} value={ui.ClassementCommand ?? ''} placeholder="/classement"
                  onChange={(e) => setUi((u) => withOptional({ ...u }, 'ClassementCommand', e.target.value))} />
              </label>
            </div>

            <div className={styles.boxHead}>
              <label className={styles.field}>
                <span className={styles.label}>Métier</span>
                <select className={styles.input} value={uiJob} onChange={(e) => setUiJob(e.target.value)}>
                  {uiJobs.length === 0 && <option value="">Aucun métier</option>}
                  {uiJobs.map((j) => <option key={j} value={j}>{jobLabel(j)}</option>)}
                </select>
              </label>
              <div className={styles.totals}>
                <span><strong>{tiers.length}</strong> palier(s) sur {MAX_TIERS} affichés</span>
                {KNOWN_JOBS.some((j) => !uiJobs.includes(j)) && (
                  <select className={styles.input} value="" style={{ width: 'auto' }}
                    onChange={(e) => addUiJob(e.target.value)} aria-label="Ajouter un métier à l'écran">
                    <option value="">+ Métier</option>
                    {KNOWN_JOBS.filter((j) => !uiJobs.includes(j)).map((j) => <option key={j} value={j}>{jobLabel(j)}</option>)}
                  </select>
                )}
              </div>
            </div>

            {ngJob && (
              <>
                <label className={styles.field} style={{ marginTop: 8 }}>
                  <span className={styles.label}>Description du métier</span>
                  <textarea className={`${styles.input} ${styles.textArea}`} rows={3} value={ngJob.Description ?? ''}
                    placeholder="Mine des blocs et fais fondre des minerais pour gagner de l'expérience."
                    onChange={(e) => setNgJob((j) => ({ ...j, Description: e.target.value }))} />
                </label>

                {tiers.length !== MAX_TIERS && (
                  <div className={styles.warnBox}>
                    <AlertTriangle size={14} /> L'écran affiche {MAX_TIERS} paliers par métier : {tiers.length < MAX_TIERS
                      ? `${MAX_TIERS - tiers.length} carte(s) resteront vides.`
                      : `les paliers au-delà du ${MAX_TIERS}e ne sont pas affichés.`}
                  </div>
                )}

                {tiers.map((t, i) => {
                  const advantages = Array.isArray(t.Advantages) ? t.Advantages : [];
                  return (
                    <div key={i} className={styles.keysPanel} style={{ marginTop: 8 }}>
                      <div className={styles.keysHead}>
                        <span className={styles.keysTitle}>Palier {i + 1}</span>
                        {rowActions(
                          () => { const n = moveIn(tiers, i, -1); if (n) setTiers(n); },
                          () => { const n = moveIn(tiers, i, 1); if (n) setTiers(n); },
                          () => { const n = [...tiers]; n.splice(i + 1, 0, JSON.parse(JSON.stringify(t)) as Tier); setTiers(n); },
                          () => { if (window.confirm(`Supprimer le palier ${i + 1} de ${jobLabel(uiJob)} ?`)) setTiers(tiers.filter((_, k) => k !== i)); },
                        )}
                      </div>
                      <div className={styles.tierGrid}>
                        <label className={styles.field}>
                          <span className={styles.label}>Niveau</span>
                          <input className={styles.input} type="number" min={0} step={1} value={t.Level ?? 0}
                            onChange={(e) => mutateTier(i, (x) => ({ ...x, Level: toInt(e.target.value) }))} />
                        </label>
                        <label className={styles.field}>
                          <span className={styles.label}>Nom du palier</span>
                          <input className={styles.input} value={t.Name ?? ''} placeholder="MINEUR DÉBUTANT"
                            onChange={(e) => mutateTier(i, (x) => ({ ...x, Name: e.target.value }))} />
                        </label>
                        <label className={styles.field}
                          title={TEXTURE_TIERS.includes(i) ? undefined : "Le client n'affiche la texture que pour les paliers 3 et 5."}>
                          <span className={styles.label}>
                            Texture de l'item débloqué{TEXTURE_TIERS.includes(i) ? '' : ' (non affichée)'}
                          </span>
                          <input className={styles.input} value={t.Texture ?? ''} placeholder="textures/items/pickaxe_electric"
                            onChange={(e) => mutateTier(i, (x) => withOptional(x, 'Texture', e.target.value))} />
                        </label>
                      </div>
                      <span className={styles.label}>Avantages ({advantages.length}/{MAX_ADVANTAGES})</span>
                      {advantages.map((a, k) => (
                        <div key={k} className={styles.enchantRow}>
                          <input className={styles.input} value={a} placeholder="+1 000$ offerts"
                            onChange={(e) => mutateTier(i, (x) => {
                              const next = [...(x.Advantages ?? [])];
                              next[k] = e.target.value;
                              return { ...x, Advantages: next };
                            })} />
                          <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} title="Retirer cet avantage"
                            onClick={() => mutateTier(i, (x) => ({ ...x, Advantages: (x.Advantages ?? []).filter((_, n) => n !== k) }))}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      ))}
                      {advantages.length > MAX_ADVANTAGES && (
                        <p className={styles.muted}>Au-delà de {MAX_ADVANTAGES}, les avantages ne sont pas affichés.</p>
                      )}
                      {advantages.length < MAX_ADVANTAGES && (
                        <div>
                          <button className={styles.secondaryBtn}
                            onClick={() => mutateTier(i, (x) => ({ ...x, Advantages: [...(x.Advantages ?? []), ''] }))}>
                            <Plus size={13} /> Avantage
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className={styles.footerActions}>
                  <button className={styles.secondaryBtn} onClick={addTier}>
                    <Plus size={14} /> Ajouter un palier
                  </button>
                </div>
              </>
            )}

            <div className={styles.tableHeadJobs} style={{ gridTemplateColumns: '1fr', marginTop: 16 }}>
              <span>Icônes des items (page « Gagner de l'expérience »)</span>
            </div>
            <p className={styles.muted}>
              Texture affichée pour chaque item des lignes d'XP ; sans entrée, l'écran montre l'identifiant de l'item.
            </p>
            <div className={styles.search}>
              <Search size={14} />
              <input value={textureFilter} placeholder="Filtrer par item ou texture"
                onChange={(e) => setTextureFilter(e.target.value)} />
            </div>
            <div className={styles.tableHeadTexture}>
              <span>Item</span>
              <span>Texture</span>
              <span />
            </div>
            {shownTextures.length === 0 && <p className={styles.muted}>Aucune icône{textureFilter ? ' pour ce filtre' : ''}.</p>}
            {shownTextures.map(({ k, v, i }) => {
              const duplicate = textures.some(([other], n) => n !== i && other === k);
              return (
                <div key={i} className={`${styles.rowEdit} ${k.trim() === '' || duplicate ? styles.rowWarn : ''}`}>
                  <div className={styles.cellsTexture}>
                    <span className={styles.picker}>
                      <SearchPicker groupId={palette[0]?.groupId ?? ''} value={k} options={itemOptions} showThumbnails
                        placeholder="minecraft:iron_ore"
                        onChange={(next) => {
                          // Deux lignes du même item se fondraient en une : refusé tant que l'autre existe.
                          if (next !== k && textures.some(([other]) => other === next)) return;
                          setTextures(renameKey(ui.ItemTextures ?? {}, k, next));
                        }} />
                    </span>
                    <span>
                      <input className={styles.input} value={v} placeholder="textures/blocks/iron_ore"
                        onChange={(e) => setTextures({ ...(ui.ItemTextures ?? {}), [k]: e.target.value })} />
                    </span>
                    <span className={styles.rowActions}>
                      <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} title="Supprimer"
                        onClick={() => {
                          const next = { ...(ui.ItemTextures ?? {}) };
                          delete next[k];
                          setTextures(next);
                        }}>
                        <Trash2 size={13} />
                      </button>
                    </span>
                  </div>
                </div>
              );
            })}

            {footer(
              <button className={styles.secondaryBtn} disabled={textures.some(([k]) => k === '')}
                title={textures.some(([k]) => k === '') ? "Complète d'abord la ligne sans item" : undefined}
                onClick={() => setTextures({ ...(ui.ItemTextures ?? {}), '': '' })}>
                <Plus size={14} /> Ajouter une icône
              </button>,
            )}
          </>
        )}
      </section>
    </div>
  );
}
